import type { AmbientDismissalRecord, AmbientKind, AmbientMuteRecord, AmbientOpportunity } from './ambientTypes'

const LS_KEY = 'line.dismissedAmbient.v1'
const MAX_ENTRIES = 200
const DISMISSAL_TTL_MS = 30 * 24 * 60 * 60 * 1000
const TARGET_COOLDOWN_MS = 24 * 60 * 60 * 1000
const OPPORTUNITY_TTL_MS = 7 * 24 * 60 * 60 * 1000
const MUTE_DURATION_MS = 14 * 24 * 60 * 60 * 1000
const MUTE_THRESHOLD = 3

interface StoredState {
  dismissals: AmbientDismissalRecord[]
  mutes: AmbientMuteRecord[]
}

const readLocal = (): StoredState => {
  if (typeof window === 'undefined') return { dismissals: [], mutes: [] }
  try {
    const raw = window.localStorage.getItem(LS_KEY)
    if (!raw) return { dismissals: [], mutes: [] }
    const parsed = JSON.parse(raw) as Partial<StoredState>
    return {
      dismissals: Array.isArray(parsed.dismissals) ? parsed.dismissals : [],
      mutes: Array.isArray(parsed.mutes) ? parsed.mutes : [],
    }
  } catch {
    return { dismissals: [], mutes: [] }
  }
}

const writeLocal = (state: StoredState) => {
  if (typeof window === 'undefined') return
  try {
    const trimmed: StoredState = {
      dismissals: state.dismissals.slice(-MAX_ENTRIES),
      mutes: state.mutes,
    }
    window.localStorage.setItem(LS_KEY, JSON.stringify(trimmed))
  } catch {
    // Quota exceeded / private mode — accept the loss.
  }
}

export const buildTargetKey = (opportunity: Pick<AmbientOpportunity, 'kind' | 'targetRefs'>): string => {
  const primary = opportunity.targetRefs[0]?.id ?? 'global'
  return `${opportunity.kind}:${primary}`
}

export const recordAmbientDismissal = (opportunity: AmbientOpportunity, now: number = Date.now()) => {
  const state = readLocal()
  const targetKey = buildTargetKey(opportunity)
  const dismissal: AmbientDismissalRecord = {
    opportunityId: opportunity.id,
    kind: opportunity.kind,
    targetKey,
    dismissedAt: now,
  }
  const kept = state.dismissals.filter((entry) => now - entry.dismissedAt < DISMISSAL_TTL_MS)
  kept.push(dismissal)

  const recentKind = kept.filter((entry) => entry.kind === opportunity.kind && now - entry.dismissedAt < DISMISSAL_TTL_MS)
  const mutes = state.mutes.filter((entry) => entry.mutedUntil > now)
  if (recentKind.length >= MUTE_THRESHOLD && !mutes.some((entry) => entry.kind === opportunity.kind)) {
    mutes.push({ kind: opportunity.kind, mutedUntil: now + MUTE_DURATION_MS, reason: 'threshold_dismissals' })
  }
  writeLocal({ dismissals: kept, mutes })
}

export const getAmbientDismissals = (now: number = Date.now()): AmbientDismissalRecord[] => {
  const state = readLocal()
  return state.dismissals.filter((entry) => now - entry.dismissedAt < DISMISSAL_TTL_MS)
}

export const getAmbientMutes = (now: number = Date.now()): AmbientMuteRecord[] => {
  const state = readLocal()
  return state.mutes.filter((entry) => entry.mutedUntil > now)
}

export const setAmbientMute = (kind: AmbientKind, mutedUntil: number) => {
  const state = readLocal()
  const mutes = state.mutes.filter((entry) => entry.kind !== kind)
  if (mutedUntil > Date.now()) mutes.push({ kind, mutedUntil, reason: 'user_settings' })
  writeLocal({ ...state, mutes })
}

export const unmuteAmbientKind = (kind: AmbientKind) => {
  const state = readLocal()
  writeLocal({ ...state, mutes: state.mutes.filter((entry) => entry.kind !== kind) })
}

/**
 * Filter opportunities against dismissals + mutes.
 * - Exact opportunity IDs dismissed in the last 7 days are dropped outright.
 * - (kind, primary target) tuple has a 24h cooldown; after that a fresh signal re-arms.
 * - Any kind with an active mute is dropped.
 */
export const applyDismissalFilter = (
  opportunities: AmbientOpportunity[],
  dismissals: AmbientDismissalRecord[],
  mutes: AmbientMuteRecord[],
  now: number = Date.now(),
): AmbientOpportunity[] => {
  const mutedKinds = new Set(mutes.filter((entry) => entry.mutedUntil > now).map((entry) => entry.kind))
  return opportunities.filter((opportunity) => {
    if (mutedKinds.has(opportunity.kind)) return false
    const targetKey = buildTargetKey(opportunity)
    const dismissedById = dismissals.find((entry) => entry.opportunityId === opportunity.id)
    if (dismissedById && now - dismissedById.dismissedAt < OPPORTUNITY_TTL_MS) return false
    const dismissedByTarget = dismissals
      .filter((entry) => entry.targetKey === targetKey)
      .sort((left, right) => right.dismissedAt - left.dismissedAt)[0]
    if (dismissedByTarget && now - dismissedByTarget.dismissedAt < TARGET_COOLDOWN_MS) return false
    return true
  })
}

export const clearAmbientDismissalStore = () => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(LS_KEY)
}
